import React, { createContext, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { getModalStyle } from "./helper";
import Spinner from "../../ui/spinner";

import "./ConfirmationModal.css";

const ConfirmationContext = createContext(null);

function ConfirmationContextProvider({ children }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [config, setConfig] = useState({
        title: "Confirm",
        message: "Are you sure?",
        confirmText: "Confirm",
        cancelText: "Cancel",
        position: "center", // center, top, bottom, left, right
        targetRect: null,
        loading: false
    });
    const [resolvePromise, setResolvePromise] = useState(null);
    const setLoadingRef = useRef(null);

    const setLoading = useCallback((loading) => {
        setConfig((prev) => ({ ...prev, loading }));
    }, []);

    useEffect(() => {
        setLoadingRef.current = setLoading;
    }, [setLoading]);

    const confirm = useCallback((options = {}, event = null) => {
        return new Promise((resolve) => {
            let targetRect = null;

            if (options.position && options.position !== "center") {
                const targetElement = event?.currentTarget || event?.target || options.targetElement;

                if (targetElement) {
                    targetRect = targetElement.getBoundingClientRect();
                }
            }

            setConfig({
                title: options.title,
                message: options.message || "Are you sure?",
                confirmText: options.confirmText || "Confirm",
                cancelText: options.cancelText || "Cancel",
                position: options.position || "center",
                targetRect,
                onConfirm: options.onConfirm,
                loading: false
            });
            setResolvePromise(() => resolve);
            
            setTimeout(() => setIsOpen(true), 0);
        });
    }, []);

    useEffect(() => {
        if (config.setLoadingFn && typeof config.setLoadingFn === 'function') {
            const updateLoading = (loading) => {
                setConfig((prev) => ({ ...prev, loading }));
            };

            config.setLoadingFn(updateLoading);
        }
    }, [config]);

    const handleConfirm = useCallback(async () => {
        if (resolvePromise) {
            if (config.onConfirm) {
                await config.onConfirm();
            }

            resolvePromise(true);
        }

        setIsClosing(true);

        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
        }, 200);
    }, [config, resolvePromise])

    const handleCancel = useCallback(() => {
        setIsClosing(true);

        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);

            if (resolvePromise) {
                resolvePromise(false);
            }
        }, 200);
    }, [resolvePromise]);

    const handleBackdropClick = useCallback((event) => {
        if (event.target === event.currentTarget) {
            handleCancel();
        }
    }, []);

    const handleEscape = useCallback((event) => {
        if (event.key === "Escape" && isOpen) {
            handleCancel();
        }
    }, [handleCancel, isOpen]);

    const confirmTitle = useMemo(() => {
        let title = React.Fragment;

        if (config.title) {
            title = (
                <h2 className="confirmation-modal__title">
                    {config.title}
                </h2>
            );
        }

        return title;
    }, [config]);

    useEffect(() => {
        document.addEventListener("keydown", handleEscape);

        return () => document.removeEventListener("keydown", handleEscape);
    }, [handleEscape]);

    const backdropClass = `confirmation-modal__backdrop ${
        config.position === "center" 
            ? "confirmation-modal__backdrop--center" 
            : "confirmation-modal__backdrop--positioned"
    } ${isClosing ? "confirmation-modal__backdrop--closing" : ""}`;

    const modalContent = isOpen && (
        <div
            className={backdropClass}
            onClick={handleBackdropClick}
        >
            <div 
                className="confirmation-modal__content"
                style={getModalStyle(config)}
            >
                {confirmTitle}

                <p className="confirmation-modal__message">
                    {config.message}
                </p>

                <div className="confirmation-modal__buttons">
                    <button
                        className="confirmation-modal__button confirmation-modal__button--confirm"
                        onClick={handleConfirm}
                        disabled={config.loading}
                    >
                        {config.loading && <Spinner />}

                        {config.confirmText}
                    </button>

                    <button
                        className="confirmation-modal__button confirmation-modal__button--cancel"
                        onClick={handleCancel}
                        disabled={config.loading}
                    >
                        {config.cancelText}
                    </button>
                </div>

                <div className={`confirmation-modal__content__triangle confirmation-modal__content__triangle--${config.position}`} />
            </div>
        </div>
    );

    return (
        <ConfirmationContext.Provider value={{ confirm, setLoading: setLoadingRef }}>
            {children}

            {isOpen && createPortal(modalContent, document.body)}
        </ConfirmationContext.Provider>
    );
}

export { ConfirmationContext, ConfirmationContextProvider };
